import { validateTableSearch } from '#/shared/lib/table'
import type { TableSearchParams } from '#/shared/lib/table'

/**
 * Date-range filter URL keys read by `useShipmentListFilters`. Includes the
 * common ranges plus every stage-date prefix (`leadDate`/`quoteDate`/
 * `orderDate`) — a page only uses its own, but preserving all of them keeps
 * the validator stage-agnostic and harmless to share.
 */
export const SHIPMENT_LIST_DATE_SEARCH_KEYS = [
  'firstShipFrom',
  'firstShipTo',
  'lastEditedFrom',
  'lastEditedTo',
  'taskDeadlineFrom',
  'taskDeadlineTo',
  'leadDateFrom',
  'leadDateTo',
  'quoteDateFrom',
  'quoteDateTo',
  'orderDateFrom',
  'orderDateTo',
  'convertedDateFrom',
  'convertedDateTo',
  'dispatchedDateFrom',
  'dispatchedDateTo',
  'pickedUpDateFrom',
  'pickedUpDateTo',
  'deliveredDateFrom',
  'deliveredDateTo',
] as const

export type ShipmentListDateSearchKey =
  (typeof SHIPMENT_LIST_DATE_SEARCH_KEYS)[number]

/**
 * String filter URL keys read by the shipment list pages. `userId` is the
 * owner filter the default-to-me writer sets; `status` is the active tab.
 * Both must survive `validateSearch` or the list query never settles.
 *
 * The trailing four are the multi-value filters, each stored as one
 * comma-joined string (`?pickupStates=CA,TX`) — the exact form the API takes.
 */
export const SHIPMENT_LIST_STRING_SEARCH_KEYS = [
  'status',
  'userId',
  'timezones',
  'pickupStates',
  'deliveryStates',
  'sources',
] as const

export type ShipmentListStringSearchKey =
  (typeof SHIPMENT_LIST_STRING_SEARCH_KEYS)[number]

export type ShipmentListSearch = TableSearchParams &
  Partial<Record<ShipmentListDateSearchKey, string>> &
  Partial<Record<ShipmentListStringSearchKey, string>>

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/**
 * `validateSearch` for every shipment list route (leads/quotes/orders/task).
 * Extends the shared table params with the owner/tab/date filters those pages
 * read from the URL. Without these keys, TanStack Router strips them on every
 * navigation — most visibly, the dropped `userId` leaves `useDefaultOwnerSettled`
 * permanently gated and the table stuck on its skeleton.
 */
export function validateShipmentListSearch(
  raw: Record<string, unknown>,
): ShipmentListSearch {
  const out: ShipmentListSearch = { ...validateTableSearch(raw) }
  for (const key of SHIPMENT_LIST_DATE_SEARCH_KEYS) {
    const value = raw[key]
    if (typeof value === 'string' && DATE_PATTERN.test(value)) {
      out[key] = value
    }
  }
  for (const key of SHIPMENT_LIST_STRING_SEARCH_KEYS) {
    const value = raw[key]
    if (typeof value === 'string' && value.length > 0) {
      out[key] = value
    }
  }
  return out
}
